import React, { useState, useEffect } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const ServicesList = ({ servicesByCategory, onBookClick, bookingLoading, currencySymbol = "$", currencyCode = "CAD" }) => {
  /*
    servicesByCategory is an object shaped like:
    {
      "Gel-X Services": [ {id, name, price, durationMinutes, basePrice, ...}, ... ],
      "Manicure Services": [...],
      "Uncategorized": [...]
    }
  */

  // Convert the object into an array of [categoryName, serviceDetails[]]
  const categories = Object.entries(servicesByCategory);

  // State for selected category tab
  const [selectedCategory, setSelectedCategory] = useState(
    categories.length > 0 ? categories[0][0] : null
  );

  // Helper function to format duration in a user-friendly way
  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return "0 mins";

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return `${hours}h ${remainingMinutes}m`;
    }
  };

  // Get services for selected category
  const selectedServices = selectedCategory
    ? servicesByCategory[selectedCategory] || []
    : [];

  // Calculate price range for a category
  const getPriceRange = (services) => {
    if (!services || services.length === 0) return null;
    const prices = services
      .map(s => parseFloat(s.basePrice || s.price || 0))
      .filter(p => p > 0);
    if (prices.length === 0) return null;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return `${currencySymbol}${min.toFixed(0)} ${currencyCode}`;
    return `from ${currencySymbol}${min.toFixed(0)} ${currencyCode}`;
  };

  // Calculate duration range for a category
  const getDurationRange = (services) => {
    if (!services || services.length === 0) return null;
    const durations = services
      .map(s => s.durationMinutes || s.duration || 0)
      .filter(d => d > 0);
    if (durations.length === 0) return null;
    const min = Math.min(...durations);
    const max = Math.max(...durations);
    if (min === max) return formatDuration(min);
    return `${formatDuration(min)} - ${formatDuration(max)}`;
  };

  if (categories.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg text-center">
        <p className="text-gray-500">No services available.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Category Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex overflow-x-auto scrollbar-hide -mb-px">
          {categories.map(([categoryName, services]) => (
            <button
              key={categoryName}
              onClick={() => setSelectedCategory(categoryName)}
              className={`
                px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                ${selectedCategory === categoryName
                  ? "border-purple-600 text-purple-600"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                }
              `}
            >
              {categoryName}
            </button>
          ))}
        </div>
      </div>

      {/* Services List - Fixed height container to prevent layout shift */}
      <div className="min-h-[400px]">
        <div className="space-y-4">
          {selectedServices.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No services in this category.</p>
            </div>
          ) : (
            selectedServices.map((service) => {
              const isLoading = bookingLoading === service.id;
              const price = parseFloat(service.basePrice || service.price || 0);
              const duration = service.durationMinutes || service.duration || 0;

              return (
                <ServiceCard
                  key={service.id}
                  service={service}
                  price={price}
                  duration={duration}
                  isLoading={isLoading}
                  onBookClick={onBookClick}
                  currencySymbol={currencySymbol}
                  currencyCode={currencyCode}
                />
              );
            })
          )}
        </div>
      </div>

      {/* Category Summary (if multiple services) */}
      {selectedServices.length > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
          <span>{selectedServices.length} services available</span>
          {getPriceRange(selectedServices) && (
            <span className="ml-4">{getPriceRange(selectedServices)}</span>
          )}
          {getDurationRange(selectedServices) && (
            <span className="ml-4">{getDurationRange(selectedServices)}</span>
          )}
        </div>
      )}
    </div>
  );
};

// Image Carousel Modal Component - Full screen image viewer
const ImageCarouselModal = ({ images, currentIndex, onClose, onPrev, onNext, setCurrentIndex }) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, onPrev, onNext]);

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) onNext();
    else if (distance < -50) onPrev();
  };

  if (!images || images.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.97)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        color: 'white',
      }}>
        <span style={{ fontSize: '16px', fontWeight: '500' }}>
          {currentIndex + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '32px',
            cursor: 'pointer',
            padding: '8px',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Main Image Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: '0 60px',
        minHeight: 0,
      }}>
        {/* Previous Button */}
        {images.length > 1 && (
          <button
            onClick={onPrev}
            style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <FaChevronLeft />
          </button>
        )}

        {/* The Image - Takes up maximum space */}
        <img
          src={images[currentIndex]}
          alt={`Service image ${currentIndex + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: '8px',
          }}
        />

        {/* Next Button */}
        {images.length > 1 && (
          <button
            onClick={onNext}
            style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <FaChevronRight />
          </button>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div style={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          overflowX: 'auto',
          background: 'linear-gradient(to top, rgba(0,0,0,1), transparent)',
        }}>
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              style={{
                flexShrink: 0,
                width: '70px',
                height: '70px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: index === currentIndex ? '3px solid white' : '3px solid transparent',
                opacity: index === currentIndex ? 1 : 0.5,
                cursor: 'pointer',
                padding: 0,
                background: '#333',
              }}
            >
              <img
                src={img}
                alt={`Thumbnail ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, price, duration, isLoading, onBookClick, currencySymbol, currencyCode }) => {
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [showAddons, setShowAddons] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showCarousel, setShowCarousel] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Parse images from service
  const getServiceImages = () => {
    if (!service.images) return [];
    try {
      const images = typeof service.images === "string"
        ? JSON.parse(service.images)
        : service.images;
      return Array.isArray(images) ? images.filter(img => img) : [];
    } catch (e) {
      return [];
    }
  };

  const images = getServiceImages();
  const hasImages = images.length > 0;

  // Helper function to format duration in a user-friendly way
  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return "0 mins";

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return `${hours}h ${remainingMinutes}m`;
    }
  };

  const handleAddonToggle = (addonId) => {
    setSelectedAddons((prev) => {
      if (prev.includes(addonId)) {
        return prev.filter((id) => id !== addonId);
      } else {
        return [...prev, addonId];
      }
    });
  };

  const handleBookClick = () => {
    onBookClick(service, selectedAddons);
  };

  const openCarousel = (index = 0) => {
    setCarouselIndex(index);
    setShowCarousel(true);
  };

  const closeCarousel = () => {
    setShowCarousel(false);
  };

  const prevImage = () => {
    setCarouselIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCarouselIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const hasAddons = service.Addons && service.Addons.length > 0;

  // Check if description is long enough to need truncation
  const descriptionCharLimit = 150;
  const hasLongDescription = service.description && service.description.length > descriptionCharLimit;
  const displayDescription = showFullDescription
    ? service.description
    : service.description?.slice(0, descriptionCharLimit);

  return (
    <>
      <div className="border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white overflow-hidden">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-start gap-4 p-4">
          {/* Service Image - Large 220x220px square with center crop */}
          {hasImages && (
            <div
              className="flex-shrink-0 cursor-pointer relative group rounded-xl overflow-hidden bg-gray-100"
              style={{ width: '220px', height: '220px', minWidth: '220px', minHeight: '220px' }}
              onClick={() => openCarousel(0)}
            >
              <img
                src={images[0]}
                alt={service.name}
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              {images.length > 1 && (
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-3 py-1.5 rounded-full font-medium z-10">
                  +{images.length - 1}
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors z-[5]" />
            </div>
          )}

          {/* Service Info */}
          <div className="flex-1 min-w-0">
            <h5 className="font-bold text-gray-900 text-lg mb-1">
              {service.name}
            </h5>
            <div className="text-sm text-gray-700 mb-3">
              {duration > 0 && <span>{formatDuration(duration)}</span>}
              {duration > 0 && price > 0 && <span> @ </span>}
              {price > 0 && (
                <span className="font-medium">{currencySymbol}{price.toFixed(2)}</span>
              )}
            </div>

            {service.description && (
              <div className="text-sm text-gray-600 leading-relaxed">
                <p>
                  {displayDescription}
                  {hasLongDescription && !showFullDescription && "..."}
                </p>
                {hasLongDescription && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="mt-2 text-sm font-semibold text-gray-900 hover:text-gray-700 uppercase tracking-wide"
                  >
                    {showFullDescription ? "SHOW LESS" : "SHOW MORE"}
                  </button>
                )}
              </div>
            )}

            {/* Addons Toggle Button */}
            {hasAddons && (
              <button
                onClick={() => setShowAddons(!showAddons)}
                className="mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                {showAddons ? 'Hide' : 'Show'} Add-ons ({service.Addons.length})
              </button>
            )}
          </div>

          {/* Book Button */}
          <button
            onClick={handleBookClick}
            disabled={isLoading}
            className="ml-4 px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px] flex items-center justify-center flex-shrink-0 text-sm uppercase tracking-wide"
          >
            {isLoading ? (
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
            ) : (
              "SELECT"
            )}
          </button>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden p-4">
          {/* Top row: Name and Select button */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <h5 className="font-bold text-gray-900 text-base flex-1">
              {service.name}
            </h5>
            <button
              onClick={handleBookClick}
              disabled={isLoading}
              className="px-4 py-1.5 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded text-sm uppercase tracking-wide flex-shrink-0 flex items-center justify-center min-w-[90px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              ) : (
                "SELECT"
              )}
            </button>
          </div>

          {/* Duration and Price */}
          <div className="text-sm text-gray-700 mb-3">
            {duration > 0 && <span>{formatDuration(duration)}</span>}
            {duration > 0 && price > 0 && <span> @ </span>}
            {price > 0 && (
              <span className="font-medium">{currencySymbol}{price.toFixed(2)}</span>
            )}
          </div>

          {/* Description */}
          {service.description && (
            <div className="text-sm text-gray-600 leading-relaxed mb-3">
              <p>
                {displayDescription}
                {hasLongDescription && !showFullDescription && "..."}
              </p>
              {hasLongDescription && (
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="mt-2 text-sm font-semibold text-gray-900 hover:text-gray-700 uppercase tracking-wide"
                >
                  {showFullDescription ? "SHOW LESS" : "SHOW ALL"}
                </button>
              )}
            </div>
          )}

          {/* Service Image - Large full width x 280px height with center crop */}
          {hasImages && (
            <div
              className="w-full cursor-pointer relative mt-3 rounded-xl overflow-hidden bg-gray-100"
              style={{ height: '280px', minHeight: '280px', maxHeight: '280px' }}
              onClick={() => openCarousel(0)}
            >
              <img
                src={images[0]}
                alt={service.name}
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
              />
              {images.length > 1 && (
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-3 py-1.5 rounded-full font-medium z-10">
                  +{images.length - 1}
                </div>
              )}
            </div>
          )}

          {/* Addons Toggle Button */}
          {hasAddons && (
            <button
              onClick={() => setShowAddons(!showAddons)}
              className="mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              {showAddons ? 'Hide' : 'Show'} Add-ons ({service.Addons.length})
            </button>
          )}
        </div>

        {/* Addons Section */}
        {showAddons && hasAddons && (
          <div className="px-4 pb-4 border-t border-gray-200 pt-4">
            <h6 className="text-sm font-semibold text-gray-900 mb-3">Add-ons</h6>
            <div className="space-y-2">
              {service.Addons.map((addon) => (
                <div key={addon.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`addon-${service.id}-${addon.id}`}
                    checked={selectedAddons.includes(addon.id)}
                    onChange={() => handleAddonToggle(addon.id)}
                    className="mr-3 accent-purple-600"
                  />
                  <label htmlFor={`addon-${service.id}-${addon.id}`} className="text-sm text-gray-700 cursor-pointer flex-1">
                    {addon.name} — <span className="text-purple-600 font-semibold">{currencySymbol}{addon.price} {currencyCode}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Carousel Modal */}
      {showCarousel && (
        <ImageCarouselModal
          images={images}
          currentIndex={carouselIndex}
          onClose={closeCarousel}
          onPrev={prevImage}
          onNext={nextImage}
          setCurrentIndex={setCarouselIndex}
        />
      )}
    </>
  );
};

export default ServicesList;
